import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/components/TailwindComponents/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    side?: 'top' | 'right' | 'bottom' | 'left';
  }
>(({ className, side = 'bottom', sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    side={side}
    sideOffset={sideOffset}
    className={cn(
      'z-50 rounded-lg bg-slate-800 px-3 py-2 text-sm text-white shadow-lg',
      'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      'origin-[--radix-tooltip-content-transform-origin]',
      'relative whitespace-normal break-words',
      className,
    )}
    {...props}
  >
    {props.children}
    {/* MUI-style Arrow using CSS borders */}
    <div
      className={cn(
        'absolute w-0 h-0 border-solid',
        // Bottom arrow (default) - pointing up
        side === 'bottom' &&
          'top-[-6px] left-1/2 transform -translate-x-1/2 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-slate-800',
        // Top arrow - pointing down
        side === 'top' &&
          'bottom-[-6px] left-1/2 transform -translate-x-1/2 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-slate-800',
        // Right arrow - pointing left (towards the trigger)
        side === 'right' &&
          'left-[-6px] top-1/2 transform -translate-y-1/2 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-slate-800',
        // Left arrow - pointing right (towards the trigger)
        side === 'left' &&
          'right-[-6px] top-1/2 transform -translate-y-1/2 border-t-[6px] border-b-[6px] border-l-[6px] border-t-transparent border-b-transparent border-l-slate-800',
      )}
    />
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export interface TwTooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: React.ReactNode;
  /**
   * The element that triggers the tooltip
   */
  children: React.ReactNode;
  /**
   * Side of the trigger where the tooltip appears
   */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Delay before showing the tooltip (in milliseconds)
   */
  delayDuration?: number;
  /**
   * Whether to skip the delay when showing the tooltip
   */
  skipDelayDuration?: number;
  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean;
  /**
   * Custom class name for the tooltip content
   */
  className?: string;
  /**
   * Max width for tooltip content. Accepts number (pixels) or any CSS width string.
   * Defaults to 400 (px).
   */
  contentMaxWidth?: number | string;
  /**
   * Custom class name for the trigger element
   */
  triggerClassName?: string;
}

export function TwTooltip({
  content,
  children,
  side = 'bottom',
  delayDuration = 100,
  skipDelayDuration = 300,
  disabled = false,
  className,
  contentMaxWidth = 400,
  triggerClassName,
}: TwTooltipProps) {
  if (disabled) {
    return <>{children}</>;
  }

  const computedMaxWidth =
    typeof contentMaxWidth === 'number'
      ? `${contentMaxWidth}px`
      : contentMaxWidth;

  return (
    <TooltipProvider
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
    >
      <Tooltip>
        <TooltipTrigger asChild className={triggerClassName}>
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={side}
          className={className}
          style={{ maxWidth: computedMaxWidth }}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
